import { useEffect, useState } from 'react';
import { _notNil } from '@/littledash';
import type {
  DataTableCellIndexCoordinate,
  DataTableService,
  DataTableStateChangeEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';

export interface UseCellEditabilityReturn {
  isEditable: boolean;
  isColumnLocked: boolean;
  isColumnReadonly: boolean;
  isTableReadonly: boolean;
}

/**
 * Computes editability flags for a cell based on table and column state
 */
const computeEditability = (
  dataTableService: DataTableService,
  coordinate: DataTableCellIndexCoordinate
): UseCellEditabilityReturn => {
  const column = dataTableService.columnByIndex(coordinate.column);
  const isTableReadonly = dataTableService.readonly;
  const isColumnLocked = column?.locked ?? false;
  const isColumnReadonly = column?.read_only ?? false;
  const isEditable = !isTableReadonly && !isColumnLocked && !isColumnReadonly;

  return {
    isEditable,
    isColumnLocked,
    isColumnReadonly,
    isTableReadonly,
  };
};

/**
 * Hook to determine if a cell is editable based on table, column, and cell state.
 * Centralizes editability logic to avoid duplication across components.
 * Subscribes to column updates to react to lock/unlock changes.
 *
 * @param coordinate - Cell index coordinate (column/row indices)
 * @returns Object containing editability flags
 */
export const useCellEditability = (coordinate: DataTableCellIndexCoordinate): UseCellEditabilityReturn => {
  const { dataTableService } = useDataTable();
  const [editability, setEditability] = useState<UseCellEditabilityReturn>(() =>
    computeEditability(dataTableService, coordinate)
  );

  useEffect(() => {
    const column = dataTableService.columnByIndex(coordinate.column);
    if (_notNil(column)) {
      const abortController = new AbortController();

      // Initialize state
      setEditability(computeEditability(dataTableService, coordinate));

      const stateChangeListener = (event: DataTableStateChangeEvent) => {
        if (event.detail.type.has('columns') && event.detail.ids.has(column.id)) {
          setEditability(computeEditability(dataTableService, coordinate));
        }
      };

      dataTableService.subscribe(DataTableEvent.TableStateChange, stateChangeListener, abortController.signal);

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, coordinate.column]);

  return editability;
};
